import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps, DialogSize } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import { useAPIPostPelaksanaanAppealApproval } from 'api/kinerja/useAPIKinerja'
import { API_PATH_NOTIFICATION_LIST } from 'api/kinerja/useAPIKinerjaNotification'
import { CardPelaksanaanStepReset } from 'app/KinerjaShared/components/CardPelaksanaanStepReset'
import { assetUrl } from 'utils/url'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'

import type {
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

const CONFIRMATION_TEXT_FORM_A =
  'Saya setuju bahwa nilai sebelumnya akan dihapus dan saya harus mengulang penilaian dan observasi dari awal.'
const CONFIRMATION_TEXT_DEFAULT =
  'Saya setuju bahwa nilai sebelumnya akan dihapus dan saya harus mengulang penilaian.'

type DialogPelaksanaanAppealApprovalProps = Omit<DialogProps, 'children'> & {
  guruName: string
  requestId?: string // identifier for rollback request
  step: TPelaksanaanAppealableStep
}

export function DialogPelaksanaanAppealApproval({
  isOpen,
  guruName,
  requestId,
  step,
  onClose,
  ...dialogProps
}: DialogPelaksanaanAppealApprovalProps) {
  const formMethods = useForm()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeContent, setActiveContent] = useState<'FORM' | 'SUCCESS'>('FORM')

  const rollbackRequestMutation = useAPIPostPelaksanaanAppealApproval(requestId)
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const SCREEN_NAME = 'ks_penilaian_home'
  const handleSubmit = () => {
    rollbackRequestMutation.mutate(
      { confirmationState: 'APPROVED' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
          queryClient.invalidateQueries(API_PATH_NOTIFICATION_LIST)
        },
        onError: () => {
          onClose()
          toast({ message: 'Gagal lanjut. Coba lagi.' })
        },
      }
    )
  }

  // Reset dialog upon dismissal
  useEffect(() => {
    if (!isOpen) {
      formMethods.reset()
      setActiveContent('FORM')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  let stepsToReset: Array<TStatePelaksanaan> = []
  switch (step) {
    case 'DOCUMENT_PREPARATION':
      stepsToReset = [
        'DOCUMENT_PREPARATION',
        'OBSERVATION_IMPLEMENTATION',
        'DOCUMENT_FOLLOW_UP',
        'DOCUMENT_REFLECTION_FOLLOW_UP',
      ]
      break
    case 'DOCUMENT_FOLLOW_UP':
      stepsToReset = ['DOCUMENT_FOLLOW_UP', 'DOCUMENT_REFLECTION_FOLLOW_UP']
      break
    case 'DOCUMENT_REFLECTION_FOLLOW_UP':
      stepsToReset = ['DOCUMENT_REFLECTION_FOLLOW_UP']
      break
  }

  const dialogSize: DialogSize =
    activeContent === 'SUCCESS' || step !== 'DOCUMENT_PREPARATION' ? 'sm' : 'md'
  const errorMessage = formMethods.formState.errors?.confirmationText?.message
  const isEmpty = !formMethods.formState.dirtyFields['confirmationText']
  const isLoadingSubmission = rollbackRequestMutation.isLoading
  const confirmationText =
    step === 'DOCUMENT_PREPARATION'
      ? CONFIRMATION_TEXT_FORM_A
      : CONFIRMATION_TEXT_DEFAULT

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size={dialogSize}
      onClose={onClose}
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeContent === 'FORM' && (
          <form
            onSubmit={formMethods.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <Text variant="display-sm">
              {!guruName
                ? 'Nilai ulang dokumen?'
                : `Nilai ulang dokumen milik ${guruName}?`}
            </Text>
            <div
              className={clsx(
                'grid gap-4',
                dialogSize === 'sm' ? 'grid-cols-1' : 'sm:grid-cols-2'
              )}
            >
              {stepsToReset.map((step, i) => (
                <CardPelaksanaanStepReset
                  step={step}
                  className="max-w-sm"
                  key={i}
                />
              ))}
            </div>
            <Text variant="body-lg" color="subdued">
              Guru dapat mengubah isi dokumen dan Anda harus mengulang observasi
              serta penilaian seluruh dokumen dari awal.
            </Text>
            <Text variant="body-lg" color="subdued">
              Mohon ketik kalimat ini pada kolom kalimat persetujuan:
            </Text>

            <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
              <Text variant="heading-sm" color="inherit">
                {confirmationText}
              </Text>
            </div>

            <div
              className={clsx(
                'space-y-1',
                dialogSize === 'sm' ? 'max-w-xs' : 'max-w-lg'
              )}
            >
              <Text as="label" htmlFor="confirmation-text">
                Kalimat persetujuan
              </Text>
              <Textarea
                isInvalid={!!errorMessage}
                errorMessage={errorMessage}
                id="confirmation-text"
                placeholder="Ketik di sini."
                rows={3}
                {...formMethods.register('confirmationText', {
                  validate: (value) =>
                    value === confirmationText ||
                    'Mohon cek ejaan dan coba lagi.',
                })}
              />
            </div>

            <div className="flex space-x-3 border-t pt-4 justify-end">
              <Button
                color="white"
                disabled={isLoadingSubmission}
                size="lg"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_home_click_reassess_back',
                    event_trigger: SCREEN_NAME,
                    screen_name: SCREEN_NAME,
                  })
                  onClose?.()
                }}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                size="lg"
                disabled={isLoadingSubmission || isEmpty}
                isLoading={isLoadingSubmission}
                type="submit"
                onClick={() =>
                  trackEvent({
                    activity_type: 'penilaian_ks_home_click_reassess_continue',
                    event_trigger: SCREEN_NAME,
                    screen_name: SCREEN_NAME,
                  })
                }
              >
                Lanjut
              </Button>
            </div>
          </form>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl(
                '/illustrations/img-reflection-documents-magnifying-glass.png'
              )}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text variant="display-sm" className="mb-4">
              Pengajuan perubahan diterima
            </Text>
            <Text variant="body-lg" color="subdued" className="mb-6">
              Setelah guru selesai mengubah isi dokumen, Anda dapat mengulang
              penilaian Anda.
            </Text>
            <Button
              size="lg"
              onClick={() => {
                onClose?.()
                trackEvent({
                  activity_type: 'penilaian_ks_home_click_reassess_understand',
                  event_trigger: SCREEN_NAME,
                  screen_name: SCREEN_NAME,
                })
              }}
            >
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
